import { postForm } from "./http.js";
import { postJson, trimBase } from "../sync/http.js";
import type { GoogleTokens, OAuthConfig } from "./types.js";

export const AUTH_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";
export const TOKEN_ENDPOINT = "https://oauth2.googleapis.com/token";
export const REVOKE_ENDPOINT = "https://oauth2.googleapis.com/revoke";

/** Recommended minimal scopes: read+write events without the heavier "restricted"
 *  full-calendar scope. `calendar.events` covers two-way event sync. */
export const DEFAULT_SCOPES = [
  "https://www.googleapis.com/auth/calendar.events",
  "https://www.googleapis.com/auth/calendar.readonly",
  "openid",
  "email",
];

export interface AuthUrlParams {
  challenge: string;
  state: string;
  redirectUri: string;
  /** Pre-fill the account chooser with a known email (re-connect). */
  loginHint?: string;
  /** Request a refresh token (`access_type=offline`). Default true — Calendar sync
   *  needs offline access. The identity-only sign-in passes false (no refresh
   *  token to store; we only read the returned ID token). */
  offline?: boolean;
  /** Override Google's consent prompt. Default "consent" (forces a refresh token
   *  on first grant). Identity sign-in passes "select_account". */
  prompt?: string;
}

/** Build the consent-screen URL for the Authorization Code + PKCE flow. No secret.
 *  `access_type=offline` + `prompt=consent` are what make Google issue a refresh
 *  token (only on the first consent). */
export function buildAuthUrl(cfg: OAuthConfig, p: AuthUrlParams): string {
  const q = new URLSearchParams({
    client_id: cfg.clientId,
    redirect_uri: p.redirectUri,
    response_type: "code",
    scope: cfg.scopes.join(" "),
    code_challenge: p.challenge,
    code_challenge_method: "S256",
    state: p.state,
    access_type: p.offline === false ? "online" : "offline",
    prompt: p.prompt ?? "consent",
    include_granted_scopes: "true",
  });
  if (p.loginHint) q.set("login_hint", p.loginHint);
  return `${AUTH_ENDPOINT}?${q.toString()}`;
}

export interface ExchangeParams {
  code: string;
  verifier: string;
  redirectUri: string;
  clientId: string;
}

/** Trade an authorization code (+ PKCE verifier) for tokens. */
export function exchangeCode(p: ExchangeParams): Promise<GoogleTokens> {
  return postForm<GoogleTokens>(TOKEN_ENDPOINT, {
    grant_type: "authorization_code",
    code: p.code,
    code_verifier: p.verifier,
    redirect_uri: p.redirectUri,
    client_id: p.clientId,
  });
}

/** Exchange a long-lived refresh token for a fresh access token. The response
 *  usually OMITS `refresh_token` — callers must keep the existing one. */
export function refreshToken(p: { refreshToken: string; clientId: string }): Promise<GoogleTokens> {
  return postForm<GoogleTokens>(TOKEN_ENDPOINT, {
    grant_type: "refresh_token",
    refresh_token: p.refreshToken,
    client_id: p.clientId,
  });
}

/** Exchange a code through OUR server's proxy (the WEB client is confidential —
 *  the secret lives on the server). Mirrors {@link exchangeCode}'s result. */
export function exchangeCodeViaProxy(
  serverUrl: string,
  p: { code: string; verifier: string; redirectUri: string },
): Promise<GoogleTokens> {
  return postJson<GoogleTokens>(`${trimBase(serverUrl)}/google/token`, {
    code: p.code,
    codeVerifier: p.verifier,
    redirectUri: p.redirectUri,
  });
}

/** Refresh the access token through OUR server's proxy (web client). */
export function refreshViaProxy(
  serverUrl: string,
  p: { refreshToken: string },
): Promise<GoogleTokens> {
  return postJson<GoogleTokens>(`${trimBase(serverUrl)}/google/refresh`, {
    refreshToken: p.refreshToken,
  });
}

/** Best-effort token revocation on disconnect. Never throws. */
export async function revokeToken(token: string): Promise<void> {
  try {
    await postForm<void>(REVOKE_ENDPOINT, { token });
  } catch {
    /* revocation is best-effort; ignore failures */
  }
}
